import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import AuthLayout from '../components/layout/AuthLayout';
import Button from '../components/ui/Button';
import { FormField, Input } from '../components/ui/Form';

const ForgotPassword = () => {
    const navigate = useNavigate();
    const { forgotPassword, resetPassword } = useAuth();

    const [step, setStep] = useState(1); // 1: Email, 2: OTP & New Password
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const handleSendOTP = async (e) => {
        e.preventDefault();
        setLoading(true);
        const result = await forgotPassword(email);
        if (result.success) {
            toast.success(result.message);
            setStep(2);
        } else {
            toast.error(result.message);
        }
        setLoading(false);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Use at least 6 characters.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('The two passwords do not match.');
            return;
        }

        setLoading(true);
        const result = await resetPassword({ email, otp, newPassword });
        if (result.success) {
            toast.success(result.message);
            navigate('/login');
        } else {
            toast.error(result.message);
        }
        setLoading(false);
    };

    return (
        <AuthLayout footnote="The code expires shortly after it is sent. Request a new one if it lapses.">
            {step === 1 ? (
                <>
                    <h1 className="text-xl font-semibold text-ink-900">Reset your password</h1>
                    <p className="text-base text-ink-500 mt-1 mb-5">
                        Enter your email address and we'll send you a six-digit code.
                    </p>

                    <form onSubmit={handleSendOTP} className="space-y-4">
                        <FormField label="Email address" htmlFor="fp-email">
                            <div className="relative">
                                <Mail
                                    size={15}
                                    aria-hidden="true"
                                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none"
                                />
                                <Input
                                    id="fp-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    className="pl-8"
                                    placeholder="name@mybank.com"
                                />
                            </div>
                        </FormField>

                        <Button type="submit" variant="primary" size="lg" block loading={loading}>
                            Send reset code
                        </Button>

                        <Link to="/login" className="btn btn-ghost btn-block">
                            Back to sign in
                        </Link>
                    </form>
                </>
            ) : (
                <>
                    <h1 className="text-xl font-semibold text-ink-900">Choose a new password</h1>
                    <p className="text-base text-ink-500 mt-1 mb-5">
                        Enter the code sent to{' '}
                        <span className="text-ink-800 font-medium">{email}</span> and set a new password.
                    </p>

                    <form onSubmit={handleResetPassword} className="space-y-4">
                        <FormField label="Verification code" htmlFor="fp-otp">
                            <input
                                id="fp-otp"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                                required
                                maxLength={6}
                                autoComplete="one-time-code"
                                placeholder="000000"
                                className="input text-center text-2xl font-semibold tracking-[0.35em] tabular"
                                style={{ height: 52 }}
                            />
                        </FormField>

                        <FormField
                            label="New password"
                            htmlFor="fp-password"
                            hint="At least 6 characters."
                            error={error}
                        >
                            <div className="relative">
                                <Input
                                    id="fp-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    className="pr-9"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded text-ink-400 hover:text-ink-700 transition-colors duration-150"
                                >
                                    {showPassword ? (
                                        <EyeOff size={15} aria-hidden="true" />
                                    ) : (
                                        <Eye size={15} aria-hidden="true" />
                                    )}
                                </button>
                            </div>
                        </FormField>

                        <FormField label="Confirm new password" htmlFor="fp-confirm">
                            <Input
                                id="fp-confirm"
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                autoComplete="new-password"
                            />
                        </FormField>

                        <Button type="submit" variant="primary" size="lg" block loading={loading}>
                            Reset password
                        </Button>

                        <Button variant="ghost" block onClick={() => setStep(1)}>
                            Use a different email
                        </Button>
                    </form>
                </>
            )}
        </AuthLayout>
    );
};

export default ForgotPassword;
